import { useEffect } from 'react';
import {
  Button,
  Section,
  SectionContent,
  SectionHeader,
  Form,
  FormField,
  FormMessage,
  FormDescription,
  Checkbox,
  Alert,
  AlertDescription,
  Separator,
} from '@predoc/cow';
import { StepperNavigation } from '@predoc/cow';
import { Info } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { usePatientRequestContext } from '../../../context/usePatientRequestContext';
import { RequestSidebar } from '../request/RequestSidebar';
import { PresignedFileUpload, FileDisplay } from '@/components';
import {
  createReleaseAuthorizationSchema,
  ReleaseAuthorizationFormData,
  ReleaseAuthorizationFormFieldProps,
} from './validation';
import { useGetPatientsPreviousDocuments } from '../../../hooks/patientsAPI';
import { AlertTitle } from '@predoc/cow';
import { fileSizeRule } from '@/utils/files';
import type { PresignedUrlResponse } from '@/types/file-upload';

export const ReleaseSection = () => {
  const {
    currentStepIndex,
    setCurrentStep,
    requestState,
    setRequestState,
    isEditMode,
  } = usePatientRequestContext();

  const { data: previousDocuments } = useGetPatientsPreviousDocuments(
    requestState.patient?.id,
  );

  const hasPreviousDocuments =
    !!previousDocuments && previousDocuments.length > 0;

  const validationSchema =
    createReleaseAuthorizationSchema(hasPreviousDocuments);

  const form = useForm<ReleaseAuthorizationFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      uploadedReleaseForm: [],
      selectedPreviousDocuments: [],
      noReleaseAuthorizationForms: undefined,
      ...requestState.release,
    },
    mode: 'onChange',
  });

  // Reset form when release data is populated in edit mode (handles async data loading)
  useEffect(() => {
    if (!isEditMode) return;

    // noReleaseAuthorizationForms is set by the mapper when release data is populated
    if (requestState.release?.noReleaseAuthorizationForms !== undefined) {
      form.reset({
        uploadedReleaseForm: [], // Not from API, needs default
        ...requestState.release,
      });
    }
  }, [isEditMode, requestState.release, form]);

  const handlePrevious = () => {
    setCurrentStep(currentStepIndex - 1);
  };

  const handleNext = form.handleSubmit((data) => {
    setRequestState({
      ...requestState,
      release: data,
    });
    setCurrentStep(currentStepIndex + 1);
  });

  return (
    <Form {...form}>
      <div className="flex h-full gap-6">
        <div className="flex-1">
          <Section className="bg-white">
            <SectionHeader className="flex flex-col gap-2">
              <h1 className="text-xl font-bold">Release Authorization</h1>
              <p className="mb-4 text-sm text-gray-500">
                Upload signed consent forms and other pertinent documents
                required to allow for permissible release of PHI.
              </p>
            </SectionHeader>
            <SectionContent>
              <div className="space-y-6">
                <div>
                  <h3 className="mb-4 text-base font-medium text-gray-900">
                    Release Authorization Form(s) & Documents
                  </h3>

                  <FormField
                    control={form.control}
                    name="uploadedReleaseForm"
                    render={({
                      field,
                    }: ReleaseAuthorizationFormFieldProps<'uploadedReleaseForm'>) => {
                      const hasFiles = field.value && field.value.length > 0;
                      const uploadedFiles = field.value || [];

                      const handleRemoveFile = (indexToRemove: number) => {
                        const updatedFiles = uploadedFiles.filter(
                          (_, index) => index !== indexToRemove,
                        );
                        field.onChange(updatedFiles);
                      };

                      const handleUploadSuccess = (
                        files: PresignedUrlResponse[],
                      ) => {
                        // Append new files to existing ones
                        const updatedFiles = [...uploadedFiles, ...files];
                        field.onChange(updatedFiles);

                        // If files are uploaded, uncheck the "no forms" checkbox
                        if (updatedFiles.length > 0) {
                          form.setValue('noReleaseAuthorizationForms', false);
                        }
                      };

                      return (
                        <div className="space-y-2">
                          <PresignedFileUpload
                            hasError={
                              !!form.formState.errors.uploadedReleaseForm
                            }
                            multiple={true}
                            disabled={false}
                            acceptedTypes=".pdf,.png,.jpg,.jpeg"
                            validationRules={[fileSizeRule(10, true)]}
                            onChange={handleUploadSuccess}
                            value={uploadedFiles}
                          />
                          <FormMessage />
                          <FormDescription>
                            Upload PDF, PNG, or JPG/JPEG files (max 10 MB total)
                          </FormDescription>

                          {hasFiles ? (
                            <div className="space-y-2">
                              {uploadedFiles.map((file, index) => (
                                <FileDisplay
                                  key={`${file.id}-${index}`}
                                  file={file}
                                  onRemove={() => handleRemoveFile(index)}
                                />
                              ))}
                            </div>
                          ) : null}
                        </div>
                      );
                    }}
                  />
                </div>

                {hasPreviousDocuments ? (
                  <div>
                    <h3 className="mb-4 text-base font-bold text-gray-900">
                      Previously Submitted Documents
                    </h3>

                    <Alert className="bg-muted/90 mb-4 border-0">
                      <Info className="h-4 w-4" />
                      <div className="flex flex-col gap-1">
                        <AlertTitle>
                          We found previously submitted documents for this
                          patient.
                        </AlertTitle>
                        <AlertDescription className="text-sm">
                          You may optionally include these as part of release
                          authorization for this patient.
                        </AlertDescription>
                      </div>
                    </Alert>

                    <Alert className="flex flex-col gap-2">
                      <FormField
                        control={form.control}
                        name="selectedPreviousDocuments"
                        render={({
                          field,
                        }: ReleaseAuthorizationFormFieldProps<'selectedPreviousDocuments'>) => {
                          const selectedIds = field.value || [];

                          const handleCheckboxChange = (
                            docId: string,
                            checked: boolean,
                          ) => {
                            const updatedSelection = checked
                              ? [...selectedIds, docId]
                              : selectedIds.filter((id) => id !== docId);
                            field.onChange(updatedSelection);
                          };

                          return (
                            <div className="w-full">
                              {previousDocuments?.map((doc, index) => (
                                <div key={doc.id} className="w-full">
                                  <Checkbox
                                    id={`previous-doc-${doc.id}`}
                                    checked={selectedIds.includes(doc.id)}
                                    onCheckedChange={(
                                      checked: boolean | 'indeterminate',
                                    ) =>
                                      handleCheckboxChange(
                                        doc.id,
                                        checked === true,
                                      )
                                    }
                                    label={doc.name}
                                  />
                                  {index < previousDocuments.length - 1 && (
                                    <Separator className="my-4 w-full" />
                                  )}
                                </div>
                              ))}
                              <FormMessage />
                            </div>
                          );
                        }}
                      />
                    </Alert>
                  </div>
                ) : null}

                <FormField
                  control={form.control}
                  name="noReleaseAuthorizationForms"
                  render={({
                    field,
                  }: ReleaseAuthorizationFormFieldProps<'noReleaseAuthorizationForms'>) => {
                    const hasUploadedFiles =
                      (form.watch('uploadedReleaseForm') || []).length > 0;
                    const hasSelectedPreviousDocs =
                      (form.watch('selectedPreviousDocuments') || []).length >
                      0;
                    const isCheckboxDisabled =
                      hasUploadedFiles || hasSelectedPreviousDocs;

                    return (
                      <div>
                        <Checkbox
                          checked={field.value || false}
                          onCheckedChange={field.onChange}
                          id="noReleaseAuthorizationForms"
                          disabled={isCheckboxDisabled}
                          label="I do not have release authorization forms"
                          description="Select this option if you don't have signed release authorization forms or have not previously uploaded forms for this patient"
                        />
                        <FormMessage />
                      </div>
                    );
                  }}
                />
              </div>
            </SectionContent>
          </Section>

          <StepperNavigation>
            <Button
              variant="outline"
              onClick={handlePrevious}
              className="flex items-center gap-2"
            >
              Previous
            </Button>
            <Button onClick={handleNext} className="flex items-center gap-2">
              Next
            </Button>
          </StepperNavigation>
        </div>

        <div className="w-1/4 flex-shrink-0">
          <RequestSidebar />
        </div>
      </div>
    </Form>
  );
};
